import React from 'react'

import { SxProps } from '@mui/material/styles'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormHelperText from '@mui/material/FormHelperText'
import FormLabel from '@mui/material/FormLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'

import colors from '@/assets/scss/_themes-vars.module.scss'

export type RadioGroupInputProps = {
  disabled?: boolean
  /**
   * The default value. Use when the component is not controlled.
   */
  defaultValue?: any
  /**
   * If `true`, the component is displayed in an error state.
   * @default false
   */
  error?: boolean
  /**
   * @default false
   */
  fullWidth?: boolean
  helperText?: React.ReactNode
  /**
   * The label content
   */
  label?: React.ReactNode
  /**
   * The name used to reference the value of the control.
   * If you don't provide this prop, it falls back to a randomly generated name.
   */
  name?: string
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  /**
   * Callback fired when a radio button is selected.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event The event source of the callback.
   * @param {string} value The value of the selected radio button.
   * You can pull out the new value by accessing `event.target.value` (string).
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, value: string) => void
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void
  /**
   * The radio options
   */
  options: { label: React.ReactNode; value: unknown }[]
  /**
   * Display group of elements in a compact row.
   */
  row?: boolean
  /**
   * Value of the selected radio button. The DOM API casts this to a string.
   */
  value?: string
  /**
   * The variant to use.
   * @default 'standard'
   */
  variant?: 'standard' | 'contained'
  sx?: SxProps
}

export function RadioGroupInput(props: RadioGroupInputProps) {
  const {
    disabled = false,
    defaultValue = '',
    error = false,
    fullWidth,
    helperText,
    label,
    name,
    onBlur,
    onChange,
    onFocus,
    options,
    row = false,
    value = '',
    variant = 'standard',
    sx,
  } = props

  return (
    <FormControl disabled={disabled} error={error} fullWidth={fullWidth} sx={sx}>
      {!!label && <FormLabel sx={{ lineHeight: '24px', fontSize: '14px', color: colors.gray700 }}>{label}</FormLabel>}
      <RadioGroup defaultValue={defaultValue} name={name} value={value} onBlur={onBlur} onChange={onChange} onFocus={onFocus} row={row}>
        {options.map((opt, idx) => (
          <FormControlLabel
            key={`${opt.value} - ${idx}`}
            value={opt.value}
            control={<Radio size="small" />}
            label={opt.label}
            sx={[
              {
                color: colors.gray900,
                fontSize: '14px',
                fontWeight: 500,
              },
              variant === 'contained' && {
                border: `1px solid ${colors.gray200}`,
                borderBottom: 'none',
                m: 0,
                p: '16px',
                '&:first-of-type': {
                  borderTopLeftRadius: '6px',
                  borderTopRightRadius: '6px',
                },
                '&:last-of-type': {
                  borderBottom: `1px solid ${colors.gray200}`,
                  borderBottomLeftRadius: '6px',
                  borderBottomRightRadius: '6px',
                },
              },
              variant === 'contained' &&
                opt.value === value && {
                  backgroundColor: colors.indigo50,
                  borderColor: colors.indigo200,
                  borderBottom: `1px solid ${colors.indigo200} !important`,
                  fontWeight: 400,
                  color: (theme) => theme.palette.primary.main,
                  '& + .MuiFormControlLabel-root': {
                    borderTopWidth: '0',
                  },
                },
              variant === 'contained' &&
                error && {
                  borderColor: (theme) => theme.palette.error.main,
                  '&:last-of-type': {
                    borderBottomColor: (theme) => theme.palette.error.main,
                  },
                },
            ]}
          />
        ))}
      </RadioGroup>
      <FormHelperText>{helperText}</FormHelperText>
    </FormControl>
  )
}

export default RadioGroupInput
